import { useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import lfSignet from "@/assets/logos/lf-signet.svg";

const teamSubItems = [
  { label: "Margret Klinkhammer", href: "/wer-wir-sind/margret" },
  { label: "Stephan Rathgeber", href: "/wer-wir-sind/stephan" },
  { label: "Burkhard Rathgeber", href: "/wer-wir-sind/burkhard" },
];

const portfolioSubItems = [
  { label: "Beratung", href: "/portfolio/beratung" },
  { label: "Qualifizierung", href: "/portfolio/qualifizierung" },
  { label: "Coaching", href: "/portfolio/coaching" },
  { label: "Supervision", href: "/portfolio/supervision" },
];

type NavItem = {
  label: string;
  sectionId: string;
  href?: string;
  children?: { label: string; href: string }[];
  hidden?: boolean;
};

const navItems: NavItem[] = [
  { label: "Home", sectionId: "home" },
  { label: "Leadership-Refugium", sectionId: "leadership-refugium", href: "/leadership-refugium" },
  { label: "Lösungen", sectionId: "portfolio", children: portfolioSubItems },
  { label: "Unsere Haltung", sectionId: "unsere-haltung" },
  { label: "Wer wir sind", sectionId: "wer-wir-sind", children: teamSubItems },
  { label: "Veröffentlichungen", sectionId: "blog" },
  { label: "Partnernetzwerk", sectionId: "partnernetzwerk" },
  { label: "Referenzen", sectionId: "partner" },
  { label: "Blog", sectionId: "blog-page", href: "/blog" },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpandedItem, setMobileExpandedItem] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToSection = useCallback(
    (sectionId: string) => {
      const isHome = location.pathname === "/";
      if (isHome) {
        const el = document.getElementById(sectionId);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      } else {
        navigate(`/#${sectionId}`);
      }
    },
    [location.pathname, navigate]
  );

  const isActiveSection = (sectionId: string) => {
    if (location.pathname === "/") return false; // on home, highlight is handled by scroll
    if (sectionId === "wer-wir-sind") return location.pathname.startsWith("/wer-wir-sind");
    if (sectionId === "portfolio") return location.pathname.startsWith("/portfolio");
    if (sectionId === "unsere-haltung") return location.pathname === "/unsere-haltung";
    if (sectionId === "partnernetzwerk") return location.pathname === "/partnernetzwerk";
    if (sectionId === "partner") return location.pathname === "/partner";
    if (sectionId === "blog") return location.pathname === "/blog";
    if (sectionId === "leadership-refugium") return location.pathname === "/leadership-refugium";
    if (sectionId === "blog-page") return location.pathname === "/blog";
    return false;
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-primary text-primary-foreground">
      <div className="container flex h-14 items-center">
        {/* Nav left-aligned */}

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center flex-1 justify-between">
          {navItems.filter((item) => !item.hidden).map((item) =>
            item.children ? (
              <div key={item.sectionId} className="relative group">
                <button
                  onClick={() => scrollToSection(item.sectionId)}
                  className={cn(
                    "px-4 py-2 text-base font-medium rounded-md transition-colors inline-flex items-center gap-1",
                    isActiveSection(item.sectionId)
                      ? "text-primary-foreground bg-primary-foreground/20"
                      : "text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
                  )}
                >
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
                </button>
                {/* Dropdown */}
                <div className="absolute left-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="bg-background border border-border rounded-lg shadow-lg py-2 min-w-[200px]">
                    {item.children.map((sub) => (
                      <Link
                        key={sub.href}
                        to={sub.href}
                        className={cn(
                          "block px-4 py-2.5 text-sm transition-colors",
                          location.pathname === sub.href
                            ? "text-primary font-semibold bg-primary/5"
                            : "text-foreground hover:bg-muted hover:text-primary"
                        )}
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : item.href ? (
              <Link
                key={item.sectionId}
                to={item.href}
                className={cn(
                  "px-4 py-2 text-base font-medium rounded-md transition-colors",
                  isActiveSection(item.sectionId)
                    ? "text-primary-foreground bg-primary-foreground/20"
                    : "text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
                )}
              >
                {item.label}
              </Link>
            ) : (
              <button
                key={item.sectionId}
                onClick={() => scrollToSection(item.sectionId)}
                className={cn(
                  "px-4 py-2 text-base font-medium rounded-md transition-colors",
                  isActiveSection(item.sectionId)
                    ? "text-primary-foreground bg-primary-foreground/20"
                    : "text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
                )}
              >
                {item.label}
              </button>
            )
          )}
        </nav>

        {/* Signet + Mobile Toggle – right */}
        <div className="ml-auto flex items-center gap-3 lg:ml-12">
          <Link to="/">
            <img src={lfSignet} alt="Leadership Focus" className="h-9 w-auto brightness-0 invert" />
          </Link>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 text-primary-foreground"
            aria-label="Menü öffnen"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-primary-foreground/20 bg-primary">
          <nav className="container py-4 flex flex-col gap-1">
            {navItems.filter((item) => !item.hidden).map((item) =>
              item.children ? (
                <div key={item.sectionId}>
                  <button
                    onClick={() =>
                      setMobileExpandedItem(
                        mobileExpandedItem === item.sectionId ? null : item.sectionId
                      )
                    }
                    className={cn(
                      "w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-md transition-colors",
                      isActiveSection(item.sectionId)
                        ? "text-primary-foreground bg-primary-foreground/20"
                        : "text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
                    )}
                  >
                    {item.label}
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 transition-transform",
                        mobileExpandedItem === item.sectionId && "rotate-180"
                      )}
                    />
                  </button>
                  {mobileExpandedItem === item.sectionId && (
                    <div className="ml-4 flex flex-col gap-1 mt-1">
                      <button
                        onClick={() => {
                          scrollToSection(item.sectionId);
                          setMobileOpen(false);
                        }}
                        className="px-4 py-2 text-sm text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10 rounded-md text-left"
                      >
                        Übersicht
                      </button>
                      {item.children.map((sub) => (
                        <Link
                          key={sub.href}
                          to={sub.href}
                          onClick={() => setMobileOpen(false)}
                          className={cn(
                            "px-4 py-2 text-sm rounded-md transition-colors",
                            location.pathname === sub.href
                              ? "text-primary-foreground bg-primary-foreground/20"
                              : "text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10"
                          )}
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : item.href ? (
                <Link
                  key={item.sectionId}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "px-4 py-3 text-sm font-medium rounded-md transition-colors text-left block",
                    isActiveSection(item.sectionId)
                      ? "text-primary-foreground bg-primary-foreground/20"
                      : "text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
                  )}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.sectionId}
                  onClick={() => {
                    scrollToSection(item.sectionId);
                    setMobileOpen(false);
                  }}
                  className={cn(
                    "px-4 py-3 text-sm font-medium rounded-md transition-colors text-left",
                    isActiveSection(item.sectionId)
                      ? "text-primary-foreground bg-primary-foreground/20"
                      : "text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
                  )}
                >
                  {item.label}
                </button>
              )
            )}
            
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
